import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { toast } from 'react-toastify';

import { PricingPlans } from '../components/pricing-plans';
import { Addons } from '../components/addons';
import { ActiveSubscription } from '../components/active-subscription';
import { TransactionHistory } from '../components/transaction-history';
import { Tabs } from '../components/tabs';
import { Wallet } from '../components/wallet';
import { SubscribeModal } from '../components/subscribe-modal';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/common/dialog';
import Button from '../components/common/button';
import { useAppSelector } from '../hooks/redux';
import { usePosthogCapture } from '../hooks/usePosthogCapture';
import {
  useGetBillingShowcaseQuery,
  useGetBillingSubscriptionOverviewQuery,
  useGetBillingPaymentsQuery,
  useGetUsageEventsQuery,
  useGetFrontendSubscriptionsQuery,
  useCreateCombinedOrderMutation,
  useCancelOrderMutation,
  useCancelSubscriptionInternalMutation,
  useActivateScheduledSubscriptionNowMutation,
  type BillingSubscriptionOverview,
} from '../services/billing.api';
import { useGetWalletBalanceQuery } from '../services/wallet.api';
import { startPaymentWidget } from '../utils/cloudPaumentUtils';
import type { ITariff } from '../store/tariff.slice';
import { BILLING_INTERNAL_API_KEY, DEV_MODE } from '../constants/env';

type AvailableAddon = {
  id: string;
  name: string;
  description: string;
  icon: 'clock' | 'zap' | 'storage' | 'code';
  amount: number;
  unit: string;
  price: number;
  popular?: boolean;
  currency?: string;
  bonusLimitPercent?: number;
  validityDays?: number | null;
};

const hasIndependentAddonValidity = (validityDays?: number | null): boolean =>
  Number.isFinite(Number(validityDays)) && Number(validityDays) > 0;

const mockAvailableAddonsBase = [
  {
    id: 'transcription-500',
    name: 'Доп. минуты транскрибации',
    description: 'Добавьте 500 минут транскрибации к аккаунту',
    icon: 'clock' as const,
    amount: 500,
    unit: 'мин',
    price: 1500,
    popular: true,
  },
  {
    id: 'transcription-1000',
    name: 'Пакет транскрибации',
    description: '1 000 минут транскрибации с выгодой',
    icon: 'clock' as const,
    amount: 1000,
    unit: 'мин',
    price: 2500,
  },
  {
    id: 'ai-credits-200',
    name: 'Пакет ИИ кредитов',
    description: 'Добавьте 200 кредитов генерации',
    icon: 'zap' as const,
    amount: 200,
    unit: 'кредитов',
    price: 1000,
  },
  {
    id: 'ai-credits-500',
    name: 'Бандл ИИ кредитов',
    description: '500 кредитов генерации с выгодой',
    icon: 'zap' as const,
    amount: 500,
    unit: 'кредитов',
    price: 2000,
    popular: true,
  },
  {
    id: 'storage-5',
    name: 'Доп. хранилище',
    description: 'Добавьте 5 GB облачного места',
    icon: 'storage' as const,
    amount: 5,
    unit: 'GB',
    price: 800,
  },
  {
    id: 'storage-20',
    name: 'Бандл хранилища',
    description: '20 GB облачного места с выгодой',
    icon: 'storage' as const,
    amount: 20,
    unit: 'GB',
    price: 2500,
  },
  {
    id: 'api-requests',
    name: 'Пакет API запросов',
    description: '25 000 дополнительных API запросов',
    icon: 'code' as const,
    amount: 25000,
    unit: 'запросов',
    price: 1200,
  },
];

const mockAvailableAddons: AvailableAddon[] = mockAvailableAddonsBase.map((addon) => ({
  ...addon,
  currency: 'RUB',
  bonusLimitPercent: 100,
}));
const mockTransactions = [
  {
    id: 'TX-001',
    date: '1 января 2026',
    description: 'Профессиональный план - ежемесячно',
    plan: 'Профессиональный план',
    amount: 2900,
    status: 'completed' as const,
    paymentMethod: 'Visa •••• 4242',
  },
  {
    id: 'TX-002',
    date: '1 января 2026',
    description: 'Дополнение: доп. хранилище',
    plan: 'Покупка дополнения',
    amount: 800,
    status: 'completed' as const,
    paymentMethod: 'Visa •••• 4242',
  },
  {
    id: 'TX-003',
    date: '15 декабря 2025',
    description: 'Дополнение: минуты транскрибации',
    plan: 'Покупка дополнения',
    amount: 1500,
    status: 'completed' as const,
    paymentMethod: 'Visa •••• 4242',
  },
  {
    id: 'TX-004',
    date: '1 декабря 2025',
    description: 'Профессиональный план - ежемесячно',
    plan: 'Профессиональный план',
    amount: 2900,
    status: 'completed' as const,
    paymentMethod: 'Visa •••• 4242',
  },
  {
    id: 'TX-005',
    date: '1 ноября 2025',
    description: 'Стартовый план - ежемесячно',
    plan: 'Стартовый план',
    amount: 900,
    status: 'completed' as const,
    paymentMethod: 'Visa •••• 4242',
  },
];

const formatPaymentDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
};

const getErrorMessage = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const dataMessage = (error as { data?: { message?: string } }).data?.message;
  if (dataMessage) {
    return dataMessage;
  }

  const message = (error as { message?: string }).message;
  return message ?? null;
};

export default function Subscriptions() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { activeTabId?: string } | null;
  const tabFromQuery = new URLSearchParams(location.search).get('tab');
  const defaultActiveTabId =
    state?.activeTabId === 'subscription' ||
    state?.activeTabId === 'plans' ||
    state?.activeTabId === 'addons' ||
    state?.activeTabId === 'transactions' ||
    state?.activeTabId === 'wallet'
      ? state.activeTabId
      : tabFromQuery === 'subscription' ||
          tabFromQuery === 'plans' ||
          tabFromQuery === 'addons' ||
          tabFromQuery === 'transactions' ||
          tabFromQuery === 'wallet'
        ? tabFromQuery
        : undefined;
  const [activeTab, setActiveTab] = useState<'subscription' | 'plans' | 'addons' | 'transactions' | 'wallet'>(
    (defaultActiveTabId as 'subscription' | 'plans' | 'addons' | 'transactions' | 'wallet') ?? 'subscription',
  );
  const {
    data: overviewData,
    isLoading: isOverviewLoading,
    isError: isOverviewError,
    refetch: refetchOverview,
  } = useGetBillingSubscriptionOverviewQuery();
  const { data: showcaseData, isLoading: isShowcaseLoading, isError: isShowcaseError } = useGetBillingShowcaseQuery();
  const {
    data: paymentsData,
    isLoading: isPaymentsLoading,
    isError: isPaymentsError,
    refetch: refetchPayments,
  } = useGetBillingPaymentsQuery();
  const { data: usageEventsData } = useGetUsageEventsQuery({ limit: 200, offset: 0 });
  const { data: subscriptionsData, refetch: refetchSubscriptions } = useGetFrontendSubscriptionsQuery();
  const [createCombinedOrder] = useCreateCombinedOrderMutation();
  const [cancelSubscriptionInternal, { isLoading: isCancelLoading }] = useCancelSubscriptionInternalMutation();
  const [activateScheduledNow, { isLoading: isActivateNowLoading }] = useActivateScheduledSubscriptionNowMutation();
  const [selectedAddonForPayment, setSelectedAddonForPayment] = useState<AvailableAddon | null>(null);
  const [selectedPlanForPayment, setSelectedPlanForPayment] = useState<ITariff | null>(null);
  const [walletRefreshKey, setWalletRefreshKey] = useState(0);
  const [showAddonWarning, setShowAddonWarning] = useState(false);
  const [showActivateNowWarning, setShowActivateNowWarning] = useState(false);
  const [pendingBonusAmount, setPendingBonusAmount] = useState<number | null>(null);
  const { data: walletBalanceData } = useGetWalletBalanceQuery();
  const walletBalance = walletBalanceData?.available ?? 0;
  const userData = useAppSelector((state) => state.authSlice.userData);
  const captureEvent = usePosthogCapture();

  useEffect(() => {
    if (!defaultActiveTabId) return;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [defaultActiveTabId, location.pathname, location.search, navigate]);

  const handleSelectPlan = (tariff: ITariff) => {
    if (tariff.price <= 0) {
      return;
    }

    setSelectedPlanForPayment(tariff);
  };

  const handleUpgrade = () => {
    setActiveTab('plans');
  };

  const handleBuyAddons = () => setActiveTab('addons');

  const handleCancel = async () => {
    if (!DEV_MODE) {
      return;
    }
    if (!BILLING_INTERNAL_API_KEY) {
      toast.error('Не задан API ключ для отмены тарифа');
      return;
    }
    const subscriptionId = overviewData?.subscription?.id;
    if (!subscriptionId) {
      toast.error('Не удалось определить текущую подписку');
      return;
    }
    try {
      await cancelSubscriptionInternal({ subscriptionId }).unwrap();
      toast.success('Тариф отменен');
      refetchOverview();
      refetchSubscriptions();
    } catch (error) {
      console.error(error);
      toast.error('Не удалось отменить тариф');
    }
  };

  useEffect(() => {
    if (activeTab === 'subscription') {
      refetchOverview();
    }
  }, [activeTab, refetchOverview]);

  useEffect(() => {
    if (activeTab === 'transactions') {
      refetchPayments();
    }
  }, [activeTab, refetchPayments]);

  useEffect(() => {
    if (activeTab === 'subscription' || activeTab === 'plans') {
      refetchSubscriptions();
    }
  }, [activeTab, refetchSubscriptions]);

  useEffect(() => {
    if (activeTab === 'wallet') {
      setWalletRefreshKey((prev) => prev + 1);
    }
  }, [activeTab]);

  const formatDate = (value: string) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('ru-RU');
  };

  const overviewServices = overviewData
    ? (overviewData.metered_features || []).map((feature) => ({
        featureCode: feature.feature_code,
        name: feature.title,
        available: Boolean(feature.access),
        used: Math.max(0, Number(feature.total_limit ?? 0) - Number(feature.remaining ?? 0)),
        limit: Number(feature.total_limit ?? 0),
        unit: feature.unit ? feature.unit.toString().replace(/минут/i, 'мин') : undefined,
      }))
    : [];
  const overviewRegularFeatures = (overviewData?.features ?? [])
    .filter((feature) => feature.access !== false)
    .map((feature) => feature.title)
    .filter((title): title is string => Boolean(title && title.trim() !== ''));
  const expiringMeteredFeatures = (overviewData?.metered_features ?? [])
    .filter((feature) => Number(feature.remaining ?? 0) > 0)
    .map((feature) => ({
      title: feature.title || feature.feature_code || 'Лимит',
      remaining: Number(feature.remaining ?? 0),
      unit: feature.unit ? feature.unit.toString().replace(/минут/i, 'мин') : '',
    }));
  const hasActiveMeteredFeatures = expiringMeteredFeatures.length > 0;

  const overviewAddons = overviewData
    ? (overviewData.addons || []).map((addon: BillingSubscriptionOverview['addons'][number]) => {
        const feature = addon.features?.[0];
        const totalLimit = feature?.total_limit ?? 0;
        const remaining = feature?.remaining ?? 0;

        return {
          name: addon.package?.name ?? 'Дополнение',
          amount: totalLimit,
          unit: feature?.unit ? feature.unit.toString().replace(/минут/i, 'мин') : '',
          used: Math.max(0, Number(totalLimit) - Number(remaining)),
          limit: Number(totalLimit),
          expiresAt: formatDate(addon.expires_at),
        };
      })
    : [];
  const hasActiveAddonRemaining = (overviewData?.addons ?? []).some((addon) =>
    (addon.features ?? []).some((feature) => Number(feature.remaining ?? 0) > 0),
  );
  const activeSubscriptionPrice = Number(overviewData?.subscription?.package?.price_amount ?? 0);
  const shouldWarnAddonCancellation = activeSubscriptionPrice <= 0 && hasActiveAddonRemaining;

  const hasCurrentSubscriptionInfo = Boolean(overviewData?.subscription?.package);
  const subscriptionPlan = overviewData?.subscription?.package?.name ?? '';
  const subscriptionPrice = Number(overviewData?.subscription?.package?.price_amount ?? 0);
  const subscriptionDurationDays = overviewData?.subscription?.package?.validity_days ?? null;
  const subscriptionEndAt = overviewData?.subscription?.end_at;
  const subscriptionEndDate =
    subscriptionEndAt === null ? undefined : subscriptionEndAt ? formatDate(subscriptionEndAt) : undefined;
  const showcaseCurrency = showcaseData?.currency ?? 'RUB';
  const addonItems = showcaseData?.packages
    ? showcaseData.packages
        .filter((pkg) => pkg.type === 'addon')
        .sort((a, b) => a.display_order - b.display_order)
        .map((pkg) => {
          const metered = pkg.metered_features?.[0];
          const limitValue = metered?.limit ? Number(metered.limit) : 0;
          const unit = metered?.unit ? metered.unit.toString().replace(/минут/i, 'мин') : '';

          return {
            id: pkg.code,
            name: pkg.name,
            description: metered?.title || 'Дополнительный ресурс',
            icon: 'clock' as const,
            amount: limitValue,
            unit,
            price: Number(pkg.price_amount ?? 0),
            popular: pkg.is_popular,
            currency: showcaseCurrency,
            bonusLimitPercent: pkg.bonus_payment_limit ?? 100,
            validityDays: pkg.validity_days,
          };
        })
    : [];

  const sortedSubscriptions = subscriptionsData
    ? [...subscriptionsData].sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime())
    : [];
  const activeSubscription = sortedSubscriptions.find((item) => item.status === 1);
  const scheduledSubscription = sortedSubscriptions.find((item) => item.status === 0);
  const hasScheduledSubscription = Boolean(scheduledSubscription);
  const runActivateScheduledNow = useCallback(async () => {
    if (!scheduledSubscription?.id) {
      toast.error('Не удалось определить запланированную подписку');
      return;
    }
    try {
      await activateScheduledNow({ subscriptionId: scheduledSubscription.id }).unwrap();
      toast.success('Запланированный тариф активирован');
      refetchOverview();
      refetchSubscriptions();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage ?? 'Не удалось активировать запланированный тариф');
    }
  }, [activateScheduledNow, refetchOverview, refetchSubscriptions, scheduledSubscription?.id]);

  const handleActivateNowClick = useCallback(() => {
    if (hasActiveMeteredFeatures) {
      setShowActivateNowWarning(true);
      return;
    }
    void runActivateScheduledNow();
  }, [hasActiveMeteredFeatures, runActivateScheduledNow]);
  const activePackageCode = overviewData?.subscription?.package?.code ?? null;
  const showcasePackages = showcaseData?.showcases?.length
    ? showcaseData.showcases.flatMap((showcase) => showcase.packages ?? [])
    : (showcaseData?.packages ?? []);
  const activePackageMeta = activePackageCode
    ? showcasePackages.find((pkg) => pkg.code === activePackageCode)
    : undefined;
  const activePackageMaxActivations = Number(activePackageMeta?.max_activations ?? Number.NaN);
  const activePackageActivationsCount = activePackageCode
    ? (subscriptionsData ?? []).filter((subscription) => subscription.package?.code === activePackageCode).length
    : 0;
  const isActivePackageMaxReached =
    Number.isFinite(activePackageMaxActivations) &&
    activePackageMaxActivations > 0 &&
    activePackageActivationsCount >= activePackageMaxActivations;

  const availableAddons = !isShowcaseError && addonItems.length ? addonItems : mockAvailableAddons;
  const handlePurchaseAddon = useCallback(
    (addonId: string) => {
      if (!userData) {
        toast.info('Авторизуйтесь, чтобы купить дополнение');
        return;
      }

      const addon = availableAddons.find((item) => item.id === addonId);
      if (!addon) {
        return;
      }

      setSelectedAddonForPayment(addon);
    },
    [availableAddons, userData],
  );

  const [cancelOrder] = useCancelOrderMutation();

  const cancelOrderOnFailure = useCallback(
    async (publicOrderId?: string) => {
      if (!publicOrderId) {
        return;
      }

      try {
        await cancelOrder({ publicOrderId }).unwrap();
      } catch (error) {
        console.error('Не удалось отменить заказ при ошибке оплаты', error);
      }
    },
    [cancelOrder],
  );

  const handleAddonPaymentConfirm = useCallback(
    async (bonusAmount: number) => {
      const addon = selectedAddonForPayment;
      if (!addon) {
        return;
      }

      if (!userData) {
        toast.info('Авторизуйтесь, чтобы купить дополнение');
        return;
      }

      try {
        const isIndependentAddon = hasIndependentAddonValidity(addon.validityDays);
        const targetSubscriptionId = overviewData?.subscription?.id;
        if (!isIndependentAddon && !targetSubscriptionId) {
          toast.error('Не удалось определить текущую подписку для покупки дополнения');
          return;
        }

        const order = await createCombinedOrder({
          package_code: addon.id,
          bonus_amount: bonusAmount,
          ...(!isIndependentAddon ? { target_subscription_id: targetSubscriptionId } : {}),
        }).unwrap();

        setSelectedAddonForPayment(null);
        startPaymentWidget({
          planId: addon.id,
          userId: userData.id,
          amount: order.payable_amount,
          currency: order.currency ?? addon.currency ?? showcaseCurrency,
          description: `Оплата дополнения ${addon.name}`,
          email: userData.email,
          transactionId: order.public_order_id,
          bonusesUsed: bonusAmount,
          onSuccess: () => {
            captureEvent('billing_v3_purchase_completed', {
              package_type: 'addon',
              package_code: addon.id,
              payable_amount: order.payable_amount,
              currency: order.currency ?? addon.currency ?? showcaseCurrency,
              bonus_amount: bonusAmount,
              is_scheduled: false,
            });
            toast.success('Дополнение успешно оформлено');
            setActiveTab('subscription');
          },
          onFail: () => {
            toast.error('Оплата дополнения не прошла');
            void cancelOrderOnFailure(order.public_order_id);
          },
        });
      } catch (error) {
        const errorMessage = getErrorMessage(error);
        toast.error(
          errorMessage
            ? `Не удалось создать заказ на оплату дополнения: ${errorMessage}`
            : 'Не удалось создать заказ на оплату дополнения',
        );
      }
    },
    [
      selectedAddonForPayment,
      userData,
      showcaseCurrency,
      createCombinedOrder,
      overviewData?.subscription?.id,
      captureEvent,
      cancelOrderOnFailure,
    ],
  );

  const processPlanPayment = useCallback(
    async (bonusAmount: number) => {
      if (!selectedPlanForPayment) {
        return;
      }

      if (!userData) {
        toast.info('Авторизуйтесь, чтобы оплатить тариф');
        return;
      }

      try {
        const activeOverviewSubscription = overviewData?.subscription;
        const activeSubscriptionPrice = Number(activeOverviewSubscription?.package?.price_amount ?? 0);
        const startAt =
          activeSubscriptionPrice > 0 && selectedPlanForPayment.price > 0 && !hasScheduledSubscription
            ? activeOverviewSubscription?.end_at
            : undefined;
        const isAddonPlan = selectedPlanForPayment.package_type === 'addon';
        const targetSubscriptionId = activeOverviewSubscription?.id;
        const isIndependentAddon = isAddonPlan && hasIndependentAddonValidity(selectedPlanForPayment.validity_days);

        if (isAddonPlan && !isIndependentAddon && !targetSubscriptionId) {
          toast.error('Не удалось определить текущую подписку для покупки дополнения');
          return;
        }

        const order = await createCombinedOrder(
          isAddonPlan
            ? {
                package_code: selectedPlanForPayment.id,
                bonus_amount: bonusAmount,
                ...(!isIndependentAddon ? { target_subscription_id: targetSubscriptionId } : {}),
              }
            : {
                package_code: selectedPlanForPayment.id,
                bonus_amount: bonusAmount,
                start_at: startAt,
              },
        ).unwrap();

        setSelectedPlanForPayment(null);
        startPaymentWidget({
          planId: selectedPlanForPayment.id,
          userId: userData.id,
          amount: order.payable_amount,
          currency: order.currency ?? selectedPlanForPayment.currency_code ?? showcaseCurrency,
          description: `Оплата тарифа ${selectedPlanForPayment.name}`,
          email: userData.email,
          transactionId: order.public_order_id,
          bonusesUsed: bonusAmount,
          onSuccess: () => {
            captureEvent('billing_v3_purchase_completed', {
              package_type: isAddonPlan ? 'addon' : 'plan',
              package_code: selectedPlanForPayment.id,
              payable_amount: order.payable_amount,
              currency: order.currency ?? selectedPlanForPayment.currency_code ?? showcaseCurrency,
              bonus_amount: bonusAmount,
              is_scheduled: Boolean(startAt),
            });
            toast.success('Тариф успешно оплачен');
            setActiveTab('subscription');
          },
          onFail: () => {
            toast.error('Оплата тарифа не прошла');
            void cancelOrderOnFailure(order.public_order_id);
          },
        });
      } catch (error) {
        const errorMessage = getErrorMessage(error);
        toast.error(
          errorMessage
            ? `Не удалось создать заказ на оплату тарифа: ${errorMessage}`
            : 'Не удалось создать заказ на оплату тарифа',
        );
      }
    },
    [
      selectedPlanForPayment,
      showcaseCurrency,
      userData,
      createCombinedOrder,
      overviewData?.subscription,
      hasScheduledSubscription,
      captureEvent,
      cancelOrderOnFailure,
    ],
  );

  const handlePlanPaymentConfirm = useCallback(
    (bonusAmount: number) => {
      if (shouldWarnAddonCancellation && selectedPlanForPayment?.price) {
        setPendingBonusAmount(bonusAmount);
        setShowAddonWarning(true);
        return;
      }

      void processPlanPayment(bonusAmount);
    },
    [processPlanPayment, selectedPlanForPayment?.price, shouldWarnAddonCancellation],
  );

  const displayActiveAddons = overviewAddons.length ? overviewAddons : [];
  const mappedPayments =
    paymentsData
      ?.filter((payment) => payment.payment_type === 'money')
      .map((payment) => {
        const card = payment.card;
        const cardLabel = card?.last_four ? `${card.type ?? 'Карта'} •••• ${card.last_four}` : 'Онлайн оплата';

        return {
          id: String(payment.payment_id ?? payment.public_order_id),
          date: formatPaymentDate(payment.created_at),
          description: payment.package?.name ?? 'Платеж',
          amount: Number(payment.amount ?? 0),
          status: 'completed' as const,
          paymentMethod: cardLabel,
          subscriptionStart: payment.package?.start_at ? formatPaymentDate(payment.package.start_at) : undefined,
          subscriptionEnd: payment.package?.end_at ? formatPaymentDate(payment.package.end_at) : undefined,
        };
      }) ?? [];
  const displayPayments = mappedPayments;

  return (
    <div className="w-full pb-10">
      <div className="w-full">
        <Tabs
          activeId={activeTab}
          onChange={(tabId) => setActiveTab(tabId as typeof activeTab)}
          navigationClassName="no-scrollbar touch-pan-x"
          tabs={[
            {
              id: 'subscription',
              title: 'Моя подписка',
              content: isOverviewLoading ? (
                <div className="rounded-[24px] border border-[#EFEFEF] bg-white p-6 text-center text-grey1">
                  Загружаем данные подписки...
                </div>
              ) : !hasCurrentSubscriptionInfo ? (
                <div className="rounded-[24px] border border-[#EFEFEF] bg-white p-6 text-center text-grey1">
                  Не удалось подгрузить информацию о текущем тарифе. Попробуйте обновить страницу.
                </div>
              ) : (
                <ActiveSubscription
                  plan={subscriptionPlan}
                  billingCycle="monthly"
                  price={subscriptionPrice}
                  durationDays={subscriptionDurationDays}
                  nextBillingDate={subscriptionEndDate}
                  scheduledPlanName={scheduledSubscription?.package?.name}
                  scheduledStartAt={scheduledSubscription?.start_at}
                  services={overviewServices}
                  availableFeatures={overviewRegularFeatures}
                  usageEvents={usageEventsData?.items ?? []}
                  activeAddons={displayActiveAddons}
                  onUpgrade={handleUpgrade}
                  onCancel={handleCancel}
                  onBuyAddons={handleBuyAddons}
                  hideAddons
                  showCancel={DEV_MODE && Boolean(overviewData?.subscription?.id)}
                  cancelLoading={isCancelLoading}
                  upgradeActionLabel={isActivePackageMaxReached ? 'Сменить тариф' : 'Продлить тариф'}
                  onActivateNow={scheduledSubscription ? handleActivateNowClick : undefined}
                  activateNowLoading={isActivateNowLoading}
                />
              ),
            },
            {
              id: 'plans',
              title: 'Тарифы',
              content: (
                <PricingPlans
                  onSelectPlan={handleSelectPlan}
                  activePackageCode={overviewData?.subscription?.package?.code}
                  activeEndAt={overviewData?.subscription?.end_at}
                  scheduledPackageCode={scheduledSubscription?.package?.code}
                  scheduledStartAt={scheduledSubscription?.start_at}
                />
              ),
            },
            {
              id: 'addons',
              title: 'Дополнения',
              content: <Addons addons={availableAddons} onPurchase={handlePurchaseAddon} />,
            },
            {
              id: 'transactions',
              title: 'Платежи',
              content: (
                <>
                  {isPaymentsLoading && (
                    <div className="rounded-[24px] border border-[#EFEFEF] bg-white p-6 text-center text-grey1">
                      Загружаем платежи...
                    </div>
                  )}
                  {!isPaymentsLoading && isPaymentsError && (
                    <div className="rounded-[24px] border border-[#EFEFEF] bg-white p-6 text-center text-grey1">
                      Не удалось загрузить платежи. Попробуйте обновить страницу.
                    </div>
                  )}
                  {!isPaymentsLoading && !isPaymentsError && <TransactionHistory transactions={displayPayments} />}
                </>
              ),
            },
            {
              id: 'wallet',
              title: 'Кошелек',
              content: <Wallet refreshKey={walletRefreshKey} />,
            },
          ].filter((tab) => tab.id !== 'addons')}
        />
        {selectedAddonForPayment && (
          <SubscribeModal
            open={Boolean(selectedAddonForPayment)}
            onOpenChange={(open) => {
              if (!open) {
                setSelectedAddonForPayment(null);
              }
            }}
            planName={selectedAddonForPayment.name}
            planPrice={selectedAddonForPayment.price}
            billingCycle="monthly"
            walletBalance={walletBalance}
            currencySymbol={selectedAddonForPayment.currency ?? showcaseCurrency}
            maxBonusPercent={selectedAddonForPayment.bonusLimitPercent}
            mode="addon"
            onConfirm={handleAddonPaymentConfirm}
          />
        )}
        {selectedPlanForPayment && (
          <SubscribeModal
            open={Boolean(selectedPlanForPayment)}
            onOpenChange={(open) => {
              if (!open) {
                setSelectedPlanForPayment(null);
              }
            }}
            planName={selectedPlanForPayment.name}
            planPrice={selectedPlanForPayment.price}
            billingCycle="monthly"
            walletBalance={walletBalance}
            durationDays={selectedPlanForPayment.duration_days}
            currencySymbol={selectedPlanForPayment.currency_symbol ?? showcaseCurrency}
            maxBonusPercent={selectedPlanForPayment.bonus_payment_limit_percent}
            mode="plan"
            onConfirm={handlePlanPaymentConfirm}
          />
        )}
        <Dialog
          open={showActivateNowWarning}
          onOpenChange={(open) => {
            if (!open) {
              setShowActivateNowWarning(false);
            }
          }}
        >
          <DialogContent className="sm:max-w-[420px]">
            <DialogHeader className="text-left">
              <DialogTitle className="text-lg font-comfortaa text-blackBase">Внимание</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-grey1 mb-4">
              При активации запланированного тарифа сейчас обнулятся следующие лимиты:
            </p>
            <ul className="text-sm text-grey1 mb-4 list-disc pl-5">
              {expiringMeteredFeatures.map((feature, index) => (
                <li key={`${feature.title}-${index}`}>
                  {feature.title}
                  {feature.remaining > 0
                    ? ` — ${feature.remaining.toLocaleString('ru-RU')}${feature.unit ? ` ${feature.unit}` : ''}`
                    : ''}
                </li>
              ))}
            </ul>
            <p className="text-sm text-grey1 mb-4">Продолжить?</p>
            <DialogFooter className="gap-3 sm:gap-4">
              <Button
                type="grey"
                className="!w-auto !h-[40px] !px-5 !py-0 !text-sm md:!text-base !items-center"
                onClick={() => setShowActivateNowWarning(false)}
              >
                Отмена
              </Button>
              <Button
                type="black"
                className="!w-auto !h-[40px] !px-6 !py-0 !text-sm md:!text-base !items-center"
                onClick={() => {
                  setShowActivateNowWarning(false);
                  void runActivateScheduledNow();
                }}
                loading={isActivateNowLoading}
              >
                Да, активировать
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog
          open={showAddonWarning}
          onOpenChange={(open) => {
            if (!open) {
              setShowAddonWarning(false);
              setPendingBonusAmount(null);
            }
          }}
        >
          <DialogContent className="sm:max-w-[420px]">
            <DialogHeader className="text-left">
              <DialogTitle className="text-lg font-comfortaa text-blackBase">Внимание</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-grey1 mb-4">
              При подключении нового тарифа все активные дополнения будут отменены. Продолжить?
            </p>
            <DialogFooter className="gap-3 sm:gap-4">
              <Button
                type="grey"
                className="!w-auto !h-[40px] !px-5 !py-0 !text-sm md:!text-base !items-center"
                onClick={() => {
                  setShowAddonWarning(false);
                  setPendingBonusAmount(null);
                }}
              >
                Отмена
              </Button>
              <Button
                type="black"
                className="!w-auto !h-[40px] !px-6 !py-0 !text-sm md:!text-base !items-center"
                onClick={() => {
                  setShowAddonWarning(false);
                  void processPlanPayment(pendingBonusAmount ?? 0);
                  setPendingBonusAmount(null);
                }}
              >
                Продолжить
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
